import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { DateFormatConstants, DateUtil, FileData, isNullOrUndefined, ServiceException } from '@uzurv/core';
import { UzurvRestConfiguration, UzurvRestService, UZURV_REST_CONFIGURATION } from '@uzurv/http';
import { Deserialize } from 'cerialize';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { DeviceInstallation, Driver, DriverAnnualRideExecutionData, Trip, User } from '../../models';
import moment from 'moment-timezone';

@Injectable({
  providedIn: 'root'
})
export class DriverManagementService extends UzurvRestService {
  readonly resourceUrl = 'driverManagement/drivers/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Retrieves the pay statements of a driver within a specified date range.
   *
   * @param driverId
   * @param startDate
   * @param endDate
   * @permission driver
   */
  getDriverPayStatements(driverId: number, startDate: Date, endDate: Date): Observable<FileData> {
    return this.get(`${this.resourceUrl}${driverId}/payStatement/`, {
      startDate: moment(startDate).format(DateFormatConstants.URL_DATE_FORMAT),
      endDate: moment(endDate).format(DateFormatConstants.URL_DATE_FORMAT)
    }).pipe(map(response => Deserialize(response, FileData)));
  }

  /**
   * Finds a driver by their id
   *
   * @param driverId
   * @permission driver_management
   */
  findDriverById(driverId: number): Observable<Driver> {
    return this.get(`${this.resourceUrl}${driverId}`).pipe(map(response => Deserialize(response, Driver)));
  }

  /**
   * Finds a driver's user by their driver id
   *
   * @param driverId
   * @permission driver_management
   */
  findUserByDriverId(driverId: number): Observable<User> {
    return this.get(`${this.resourceUrl}${driverId}/user/`).pipe(map(response => Deserialize(response, User)));
  }

  /**
   * Returns a list of device installations for a driver
   *
   * @param driverId
   * @permission driver_management
   */
  listDeviceInstallationsByDriverId(driverId: number): Observable<Array<DeviceInstallation>> {
    if (isNullOrUndefined(driverId)) {
      return throwError(new ServiceException('Driver id Cannot Be Null'));
    }

    return this.get(`${this.resourceUrl}${driverId}/deviceInstallations/`).pipe(map(response => Deserialize(response, DeviceInstallation)));
  }

  /**
   * Returns a list of DriverAnnualRideExecutionData for a driver
   *
   * @param driverId
   * @permission driver_tax_information_read
   */
  listAnnualRideExecutionDataByDriverId(driverId: number): Observable<Array<DriverAnnualRideExecutionData>> {
    if (isNullOrUndefined(driverId)) {
      return throwError(new ServiceException('Driver id Cannot Be Null'));
    }

    return this.get(`${this.resourceUrl}${driverId}/annualRideExecutionData/`).pipe(
      map(response => Deserialize(response, DriverAnnualRideExecutionData))
    );
  }

  /**
   * Returns a list of reservations for a specific driver id.
   *
   * @param driverId
   * @param pageNumber
   * @param pageSize
   * @param date
   * @permission reservation_read, driver_management
   */
  listReservationsByDriverId(driverId: number, pageNumber: number, pageSize: number, date: Date = null): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(`${this.resourceUrl}${driverId}/reservations/`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      date: DateUtil.serializeLocalDate(date)
    }).pipe(map(response => Deserialize(response, Trip)));
  }

  /**
   * Returns a list of reservations for the driver journey modal.
   *
   * @param driverId
   * @param activeSortedByPickupAsc
   * @param pageNumber
   * @param pageSize
   * @param date
   * @permission reservation_read, driver_management
   */
  listActiveSortedByPickupDateAscByDriverId(
    driverId: number,
    activeSortedByPickupAsc: boolean = null,
    pageNumber: number,
    pageSize: number,
    date: Date = null
  ): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(`${this.resourceUrl}${driverId}/reservations/`, {
      activeSortedByPickupAsc: activeSortedByPickupAsc,
      pageNumber: pageNumber,
      pageSize: pageSize,
      date: DateUtil.serializeLocalDate(date)
    }).pipe(map(response => Deserialize(response, Trip)));
  }
}
